// AppWithSSO.js - Main App with SSO Support (Hybrid Login)
import React, { useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import Login from "./Login";
import AgentAssist from "./AgentAssist";
import TicketDetailsPage from "./TicketDetailsPage";
import Analytics from './Analytics';
import ScreenTimeAnalytics from './ScreenTimeAnalytics';
import useIdleTimer from './hooks/useIdleTimer';
import useSessionTracking from './hooks/useSessionTracking';

function AppContent() {
  const isAuthenticated = useIsAuthenticated();
  const { instance, accounts } = useMsal();

  // Email/password login state - declare early
  const [isLoggedIn, setIsLoggedIn] = useState(() => {
    return localStorage.getItem('isLoggedIn') === 'true';
  });

  // Get user email early for session tracking
  const getUserEmail = () => {
    if (isAuthenticated && accounts && accounts.length > 0) {
      return accounts[0].username || accounts[0].email || "user@example.com";
    } else if (isLoggedIn) {
      return localStorage.getItem('userEmail') || "user@example.com";
    }
    return null;
  };

  const userEmail = getUserEmail();
  const userAuthenticated = isAuthenticated || isLoggedIn;

  // Track user session and screen time - get endSession function
  const { endSession } = useSessionTracking(userAuthenticated ? userEmail : null);

  // Handle logout for SSO
  const handleLogout = async () => {
    // End session before logging out
    if (endSession) {
      await endSession();
    }
    instance.logoutRedirect({
      postLogoutRedirectUri: "/",
    });
  };
  
  const handleLogin = (email, password) => {
    if (email === "admin@example.com" && password === "password") {
      setIsLoggedIn(true);
      localStorage.setItem('isLoggedIn', 'true');
      localStorage.setItem('userEmail', email);
      return null;
    } else {
      return "Invalid credentials. Try authorised ID and Password";
    }
  };

  // Handle logout for email/password
  const handleEmailLogout = async () => {
    // End session before logging out
    if (endSession) {
      await endSession();
    }
    setIsLoggedIn(false);
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('userEmail');
  };

  // Determine which logout handler to use
  const currentLogoutHandler = isAuthenticated ? handleLogout : handleEmailLogout;

  // Auto-logout after 15 minutes of inactivity
  useIdleTimer(() => {
    if (userAuthenticated) {
      alert('You have been logged out due to inactivity.');
      currentLogoutHandler();
    }
  }, 15 * 60 * 1000); // 15 minutes in milliseconds

  // Get user name from SSO account or email login
  const getUserName = () => {
    if (isAuthenticated && accounts && accounts.length > 0) {
      return accounts[0].name || accounts[0].username || "User";
    } else if (isLoggedIn) {
      return localStorage.getItem('userEmail')?.split('@')[0] || "User";
    }
    return "User";
  };

  const userName = getUserName();

  return (
    <>
      <Routes>
        {/* Login Route - Hybrid login page with both SSO and email/password */}
        <Route 
          path="/" 
          element={
            userAuthenticated ? (
              <Navigate to="/AgentAssist" replace />
            ) : (
              <Login onLogin={handleLogin} />
            )
          } 
        />

        {/* Protected Routes */}
        <Route 
          path="/AgentAssist" 
          element={
            userAuthenticated ? (
              <AgentAssist onLogout={currentLogoutHandler} userName={userName} userEmail={userEmail} />
            ) : (
              <Navigate to="/" replace />
            )
          } 
        />
        
        <Route 
          path="/process/:ticketId" 
          element={
            userAuthenticated ? (
              <TicketDetailsPage userName={userName} userEmail={userEmail} onLogout={currentLogoutHandler} />
            ) : (
              <Navigate to="/" replace />
            )
          } 
        />
        
        <Route 
          path="/analytics" 
          element={
            userAuthenticated ? (
              <Analytics userName={userName} userEmail={userEmail} onLogout={currentLogoutHandler} />
            ) : (
              <Navigate to="/" replace />
            )
          } 
        />
        
        <Route 
          path="/screen-time-analytics" 
          element={
            userAuthenticated ? (
              <ScreenTimeAnalytics userName={userName} userEmail={userEmail} onLogout={currentLogoutHandler} />
            ) : (
              <Navigate to="/" replace />
            )
          } 
        />
      </Routes>
    </>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
